// src/pages/RaidJobAverages.jsx
import React, { useEffect, useMemo, useState } from 'react';
import PowerRangeFilter from '../components/PowerRangeFilter';
import { useAuth } from '../contexts/AuthContext';
import { getAllRaids /*, getMyRaids */ } from '../firebaseClient';

// ====== BattleAnalyzer와 동일 유틸 ======
function normalizeTraitName(name) {
  const n = String(name || '').replace(/\s+/g, '').toLowerCase();
  if (n === '1분피해량' || n === '1분dps') return '1분 피해량';
  if (n === '1분초당피해량') return '1분 초당 피해량';
  if (n === '초당피해량' || n === 'dps') return '초당 피해량';
  return name || '';
}
function groupTraitsByName(records) {
  const bucket = {};
  records.forEach(r => {
    const all = [].concat(r.mainTraits || [], r.additionalTraits || []);
    all.forEach(t => {
      const name = normalizeTraitName((t.trait || '').trim());
      if (!name) return;
      const val = String(t.value || '');
      const raw = parseRawValue(val);
      const unit = (val.match(/[^0-9.,]+$/) || [''])[0];
      if (!bucket[name]) bucket[name] = [];
      bucket[name].push({ raw, unit });
    });
  });
  return bucket;
}
function calcTraitAverages(grouped) {
  const result = [];
  Object.keys(grouped).forEach(name => {
    const arr = grouped[name];
    if (!arr.length) return;
    const sum = arr.reduce((s, v) => s + (Number.isFinite(v.raw) ? v.raw : 0), 0);
    const count = arr.length;
    const avgRaw = count ? sum / count : 0;
    const unit = arr.find(v => v.unit)?.unit || '';
    let displayValue;
    if (name.includes('횟수') || unit === '회') {
      displayValue = Math.round(avgRaw) + '회';
    } else if (unit === '%') {
      displayValue = (avgRaw).toFixed(2) + '%';
    } else if (unit === '억' || unit === '만' || unit === '조') {
      const v = formatByUnit(avgRaw, unit);
      displayValue = v.toFixed(2) + unit;
    } else {
      displayValue = avgRaw.toFixed(2);
    }
    result.push({ trait: name, value: displayValue });
  });
  return result.sort((a, b) => a.trait.localeCompare(b.trait));
}
function parseRawValue(str) {
  const match = String(str || '').match(/^([0-9.,]+)\s*([^0-9.,]*)$/);
  const number = match ? match[1] : '';
  const unitRaw = match ? (match[2] || '').replace(/,/g, '') : '';
  const n = parseFloat((number || '').replace(/,/g, '')) || 0;
  switch (unitRaw) {
    case '조': return n * 1e12;
    case '억': return n * 1e8;
    case '만': return n * 1e4;
    default: return n;
  }
}
function formatByUnit(raw, unit) {
  const m = unit === '조' ? 1e12 : unit === '억' ? 1e8 : unit === '만' ? 1e4 : 1;
  return raw / m;
}
function getTraitRawFromRecord(record, traitNameCandidates) {
  const traits = []
    .concat(record?.mainTraits || [])
    .concat(record?.additionalTraits || []);
  for (const name of traitNameCandidates) {
    const t = traits.find(x => (x.trait || '').trim() === name);
    if (t) return parseRawValue(String(t.value || ''));
  }
  return null;
}
function formatCompactKR(raw) {
  if (raw == null || isNaN(raw)) return '-';
  const n = Number(raw);
  if (Math.abs(n) >= 1e12) return (n / 1e12).toFixed(2) + '조';
  if (Math.abs(n) >= 1e8) return (n / 1e8).toFixed(2) + '억';
  if (Math.abs(n) >= 1e4) return (n / 1e4).toFixed(2) + '만';
  return n.toLocaleString();
}
function parsePowerFloat(powerStr) {
  if (powerStr == null) return null;
  const clean = String(powerStr).replace(/,/g, '').trim();
  const m = clean.match(/^(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : null;
}

function formatPower(raw) {
  if (raw == null || isNaN(raw)) return '-';
  let s = Number(raw).toFixed(2);
  s = s.replace(/\.00$/, '').replace(/(\.\d)0$/, '$1'); // .00 제거, .10 -> .1
  return s; // 콤마 없음
}

export default function RaidJobAverages() {
  const { user: authUser } = useAuth();

  const [all, setAll] = useState([]);
  const [raid, setRaid] = useState('');
  const [job, setJob] = useState('');
  const [jobEng, setJobEng] = useState('');
  const [damageMetric] = useState('초당 피해량');
  // 전투력 범위 상태 (0 ~ 5500)
  const [powerRange, setPowerRange] = useState({ min: 0, max: 5500 });

  useEffect(() => {
    (async () => {
      try {
      const res = await getAllRaids({ take: 300 }); // 최초 페이지 사이즈는 취향껏
      const docs = Array.isArray(res) ? res : (res?.items ?? []);
      setAll(docs);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [authUser]);

  const raids = useMemo(
    () => Array.from(new Set(all.map(r => r.raidName).filter(Boolean))).sort(),
    [all]
  );

  const jobs = useMemo(() => {
    if (!raid) return [];
    return Array.from(
      new Set(all.filter(r => r.raidName === raid).map(r => r.job).filter(Boolean))
    ).sort();
  }, [all, raid]);

  const jobEngravings = useMemo(() => {
    if (!raid || !job) return [];
    return Array.from(
      new Set(
        all
          .filter(r => r.raidName === raid && r.job === job)
          .map(r => r.jobEngraving || '')
          .filter(Boolean)
      )
    ).sort();
  }, [all, raid, job]);

  const targetRecords = useMemo(() => {
    if (!raid || !job) return [];
    return all.filter(r =>
      r.raidName === raid &&
      r.job === job &&
      (jobEng ? (r.jobEngraving || '') === jobEng : true)
    );
  }, [all, raid, job, jobEng]);

  const averages = useMemo(() => {
    if (!targetRecords.length) return [];
    const grouped = groupTraitsByName(targetRecords);
    const ALLOWED_DAMAGE = new Set(['1분 피해량', '1분 초당 피해량', '초당 피해량']);
    Object.keys(grouped).forEach(name => {
      const noSpaceLower = name.replace(/\s+/g, '').toLowerCase();
      const isDamageLike = (name.includes('피해') && name.includes('량')) || noSpaceLower.includes('dps');
      if (isDamageLike && !ALLOWED_DAMAGE.has(name)) {
        delete grouped[name];
      }
    });
    return calcTraitAverages(grouped);
  }, [targetRecords]);

  const bothSelected = Boolean(raid && job);

  const damageTraitCandidates = useMemo(
    () => [damageMetric, '총 피해량', '누적 피해량', '피해 누적량'],
    [damageMetric]
  );

  // 선택한 전투력 범위로 순위 집계
  const rankingByRange = useMemo(() => {
    if (!raid) return [];
    const rows = all.filter(r => r.raidName === raid);
    const acc = new Map(); // key = `${job}|${jobEngraving}`

    rows.forEach(r => {
      const power = parsePowerFloat(r.power);
      if (!Number.isFinite(power)) return;
      if (power < powerRange.min || power > powerRange.max) return;

      const dmg = getTraitRawFromRecord(r, damageTraitCandidates);
      if (dmg == null) return;

      const key = `${r.job || '미확인'}|${r.jobEngraving || '미확인'}`;
      if (!acc.has(key)) {
        acc.set(key, {
          job: r.job || '미확인',
          jobEngraving: r.jobEngraving || '미확인',
          sum: 0, cnt: 0, powerSum: 0,
        });
      }
      const g = acc.get(key);
      g.sum += dmg;
      g.cnt += 1;
      g.powerSum += power;
    });

    return Array.from(acc.values())
      .map(g => ({ ...g, avg: g.cnt ? g.sum / g.cnt : 0, avgPower: g.cnt ? g.powerSum / g.cnt : null }))
      .sort((a, b) => b.avg - a.avg);
  }, [all, raid, powerRange, damageTraitCandidates]);


  // ===== 스타일 공통 =====

  const chevronSvg = encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="#9CA3AF"><path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.17l3.71-2.94a.75.75 0 1 1 .92 1.18l-4.17 3.31a.75.75 0 0 1-.92 0L5.21 8.41a.75.75 0 0 1 .02-1.2z"/></svg>'
  );

  const selectCls =
    // 레이아웃/모양
    "appearance-none inline-block rounded-lg border border-zinc-950/10 cursor-pointer " +
    "px-4 py-2 pr-8 bg-white " +
    // 폰트/컬러
    "text-sm font-semibold text-zinc-950 transition-all " +
    // 상호작용
    "focus:outline-none focus:ring-1 focus:ring-indigo-500 " +
    "disabled:bg-zinc-100 disabled:text-zinc-300 disabled:cursor-not-allowed " +
    // 화살표 배치
    "bg-no-repeat bg-[length:16px_16px] bg-[right_8px_center] " +
    // 🔽 다크모드
    "dark:bg-zinc-900 dark:text-zinc-100 dark:border-white/10 " +
    "dark:focus:ring-indigo-400 dark:disabled:bg-white/5 dark:disabled:text-zinc-500";

  // ===== disabled 플래그 =====
  const jobDisabled = !raid;
  const engDisabled = !raid || !job;

  return (
    <div className="mx-auto max-w-6xl sm:px-4 lg:px-0">
      <h2 className="text-lg/8 font-semibold text-zinc-950 sm:text-xl/8 dark:text-white mb-3 lg:mb-6">전투 분석 통계</h2>

      {/* 필터 (커스텀 셀렉트 3종) */}
      <div className="flex flex-wrap items-center gap-2">
        {/* 레이드 */}
        <select
          value={raid}
          onChange={(e) => { setRaid(e.target.value); setJob(''); setJobEng(''); }}
          className={`${selectCls} w-full sm:w-auto`}
          style={{
            backgroundImage: `url("data:image/svg+xml,${chevronSvg}")`,
            minWidth: '12rem',
          }}
          title="레이드 선택"
        >
          <option value="">레이드 선택</option>
          {raids.map(r => <option key={r} value={r}>{r}</option>)}
        </select>

        {/* 직업 */}
        <select
          value={job}
          onChange={(e) => { setJob(e.target.value); setJobEng(''); }}
          disabled={jobDisabled}
          className={`${selectCls} w-full sm:w-auto`}
          style={{
            backgroundImage: `url("data:image/svg+xml,${chevronSvg}")`,
            minWidth: '12rem',
          }}
          title="직업 선택"
        >
          <option value="">{raid ? '직업 선택' : '레이드 먼저 선택'}</option>
          {jobs.map(j => <option key={j} value={j}>{j}</option>)}
        </select>

        {/* 직업각인 */}
        <select
          value={jobEng}
          onChange={(e) => setJobEng(e.target.value)}
          disabled={engDisabled}
          className={`${selectCls} w-full sm:w-auto`}
          style={{
            backgroundImage: `url("data:image/svg+xml,${chevronSvg}")`,
            minWidth: '14rem',
          }}
          title="직업각인 선택"
        >
          <option value="">{job ? '직업각인 (전체)' : '직업 먼저 선택'}</option>
          {jobEngravings.map(eng => <option key={eng} value={eng}>{eng}</option>)}
        </select>
      </div>

      {/* 평균 표 카드 */}
      {!bothSelected ? (
        <></>
      ) : averages.length ? (
        <div className="mt-6 rounded-lg border border-zinc-950/10 bg-white overflow-hidden dark:bg-zinc-900/40 dark:border-zinc-300/10">
          <div className="p-3">
<div
  className="
    mb-3
    flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-3
    text-base text-zinc-700 dark:text-zinc-300
    border-b border-zinc-200 dark:border-zinc-700 pb-2
  "
>
  {/* 왼쪽 : 레이드 / 직업 */}
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
    <div>
      <span className="text-zinc-500 dark:text-zinc-400 mr-1">레이드:</span>
      <b className="text-zinc-900 dark:text-white">{raid || '-'}</b>
    </div>
    <div className="hidden sm:block text-zinc-400">|</div>
    <div>
      <span className="text-zinc-500 dark:text-zinc-400 mr-1">직업:</span>
      <b className="text-zinc-900 dark:text-white">
        {job || '-'}{jobEng ? ` / ${jobEng}` : ''}
      </b>
    </div>
  </div>

  {/* 오른쪽 : 표본 */}
  {raid && job ? (
    <div className="mt-2 sm:mt-0 sm:text-right">
      <span className="text-zinc-500 dark:text-zinc-400 mr-1">표본:</span>
      <b className="text-zinc-900 dark:text-white">{targetRecords.length}건</b>
    </div>
  ) : null}
</div>
            {/* 모바일 리스트 뷰 */}
            <div className="sm:hidden">
              <ul className="divide-y divide-zinc-200 dark:divide-zinc-800 rounded-lg overflow-hidden">
                {averages.map(row => (
                  <li key={row.trait} className="py-3 px-3 odd:bg-zinc-100/50 dark:odd:bg-zinc-800/60">
                    <div className="text-sm text-zinc-500 dark:text-zinc-400 mb-1">{row.trait}</div>
                    <div className="text-lg font-semibold text-zinc-900 dark:text-white">{row.value}</div>
                  </li>
                ))}
              </ul>
            </div>
            {/* PC 테이블 뷰 */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full table-fixed text-base">
                <colgroup>
                  <col style={{ width: '68%' }} />
                  <col style={{ width: '32%' }} />
                </colgroup>
                <thead className="bg-zinc-50 dark:bg-zinc-800/40">
                  <tr className="text-zinc-600 dark:text-zinc-300">
                    <th className="px-3 py-2 text-left font-medium">특성</th>
                    <th className="px-3 py-2 text-left font-medium">평균</th>
                  </tr>
                </thead>
                <tbody>
                  {averages.map(row => (
                    <tr key={row.trait} className="text-zinc-800 dark:text-zinc-200">
                      <td className="px-3 py-2 border-t border-zinc-950/10 dark:border-white/10">
                        <span className="block truncate" title={row.trait}>{row.trait}</span>
                      </td>
                      <td className="px-3 py-2 border-t border-zinc-950/10 dark:border-white/10">
                        <span className="block truncate" title={row.value}>{row.value}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4 text-base text-zinc-500 dark:text-zinc-400">조건에 맞는 데이터가 없습니다.</div>
      )}

      {/* 전투력 구간별 랭킹 */}
      {raid ? (
        <div className="mt-6 rounded-lg border border-zinc-950/10 bg-white overflow-hidden dark:bg-zinc-900/40 dark:border-zinc-300/10">
          <div className="p-3">
            <h3 className="text-base font-semibold text-zinc-900 mb-1 dark:text-white">
              전투력 <b>범위</b>별 데미지 랭킹
            </h3>
<div
  className="
    mb-3
    flex flex-col sm:flex-row sm:items-center sm:justify-between sm:gap-3
    text-base text-zinc-700 dark:text-zinc-300
    border-b border-zinc-200 dark:border-zinc-700 pb-2
  "
>
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
    <div>
      <span className="text-zinc-500 dark:text-zinc-400 mr-1">레이드:</span>
      <b className="text-zinc-900 dark:text-white">{raid || '-'}</b>
    </div>
    <div className="hidden sm:block text-zinc-400">|</div>
    <div>
      <span className="text-zinc-500 dark:text-zinc-400 mr-1">지표:</span>
      <b className="text-zinc-900 dark:text-white">{damageMetric}</b>
    </div>
  </div>
</div>

            {/* 전투력 범위 슬라이더 + 입력 */}
            <div className="mb-3">
              <PowerRangeFilter value={powerRange} onChange={setPowerRange} />
            </div>

            {/* 범위 내 순위 테이블 */}
            {rankingByRange.length ? (
              <>
                {/* 모바일 리스트 뷰 */}
                <div className="sm:hidden">
                  <ol className="divide-y divide-zinc-200 dark:divide-zinc-800 rounded-lg overflow-hidden">
                    {rankingByRange.map((row, idx) => (
                      <li key={`${row.job}|${row.jobEngraving}`} className="py-3 px-3 odd:bg-zinc-100/50 dark:odd:bg-zinc-800/60">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">#{idx + 1}</span>
                          <span className="text-sm text-zinc-500 dark:text-zinc-400">표본 {row.cnt}</span>
                        </div>
                        <div className="text-sm text-zinc-600 dark:text-zinc-300">
                          <b className="text-zinc-900 dark:text-white">{row.job}</b>
                          <span className="mx-1 text-zinc-400">/</span>
                          <span title={row.jobEngraving}>{row.jobEngraving}</span>
                        </div>
                        <div className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                          평균 전투력 <b className="text-zinc-900 dark:text-white">{formatPower(row.avgPower)}</b>
                        </div>
                        <div className="mt-0.5 text-base font-semibold text-zinc-900 dark:text-white">
                          평균 {damageMetric}: {formatCompactKR(row.avg)}
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
                {/* PC 테이블 뷰 */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="min-w-full table-fixed text-base">
                    <colgroup>
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '24%' }} />
                      <col style={{ width: '14%' }} />
                    </colgroup>
                    <thead className="bg-zinc-50 dark:bg-zinc-800/40">
                      <tr className="text-zinc-600 dark:text-zinc-300">
                        <th className="px-3 py-2 text-left font-medium">순위</th>
                        <th className="px-3 py-2 text-left font-medium">직업</th>
                        <th className="px-3 py-2 text-left font-medium">직업각인</th>
                        <th className="px-3 py-2 text-left font-medium">평균 전투력</th>
                        <th className="px-3 py-2 text-left font-medium">평균 {damageMetric}</th>
                        <th className="px-3 py-2 text-left font-medium">표본수</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankingByRange.map((row, idx) => (
                        <tr key={`${row.job}|${row.jobEngraving}`} className="text-zinc-800 dark:text-zinc-200">
                          <td className="px-3 py-2 border-t border-zinc-950/10 dark:border-white/10">
                            <span className="block truncate" title={String(idx + 1)}>{idx + 1}</span>
                          </td>
                          <td className="px-3 py-2 border-t border-zinc-950/10 dark:border-white/10">
                            <span className="block truncate" title={row.job}>{row.job}</span>
                          </td>
                          <td className="px-3 py-2 border-t border-zinc-950/10 dark:border-white/10">
                            <span className="block truncate" title={row.jobEngraving}>{row.jobEngraving}</span>
                          </td>
                          <td className="px-3 py-2 border-t border-zinc-950/10 dark:border-white/10">
                            <span className="block truncate" title={formatPower(row.avgPower)}>
                              {formatPower(row.avgPower)}
                            </span>
                          </td>
                          <td className="px-3 py-2 border-t border-zinc-950/10 dark:border-white/10">
                            <span className="block truncate" title={formatCompactKR(row.avg)}>
                              {formatCompactKR(row.avg)}
                            </span>
                          </td>
                          <td className="px-3 py-2 border-t border-zinc-950/10 dark:border-white/10">
                            <span className="block truncate" title={String(row.cnt)}>{row.cnt}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="text-base text-zinc-500 dark:text-zinc-400">
                {powerRange.min.toLocaleString()} ~ {powerRange.max.toLocaleString()} 범위에 해당하는 데이터가 없습니다.
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
